import { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { useChmodItem } from './hooks/use_filemanager';

const ChmodModal = ({ item, currentPath, onClose, baseApiUrl }) => {
  const [mode, setMode] = useState(item.mode);
  const chmodItemMutation = useChmodItem(baseApiUrl);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!/^[0-7]{3,4}$/.test(mode)) {
      alert('Ungültige Berechtigungen. Bitte verwenden Sie das oktale Format (z.B. 755 oder 0755)');
      return;
    }

    if (mode === item.mode) {
      onClose();
      return;
    }

    chmodItemMutation.mutate(
      { path: item.path, mode, currentPath },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const handlePresetClick = (presetMode) => {
    setMode(presetMode);
  };

  const getPermissions = () => {
    // Normalize mode string to get last 3 digits
    let str = mode.toString();
    while (str.length < 3) str = '0' + str;
    const relevant = str.slice(-3);

    const parseDigit = (d) => {
      const n = parseInt(d, 10);
      return {
        r: !!(n & 4),
        w: !!(n & 2),
        x: !!(n & 1),
      };
    };

    return {
      user: parseDigit(relevant[0]),
      group: parseDigit(relevant[1]),
      other: parseDigit(relevant[2]),
    };
  };

  const togglePermission = (scopeIndex, bitValue) => {
    let str = mode.toString();
    // Ensure we have at least 3 digits to work with
    while (str.length < 3) str = '0' + str;

    const digits = str.split('');
    // Calculate index from the end: -3 is user, -2 is group, -1 is other
    // If length is 4 (e.g. 0755), user is at index 1.
    const targetIndex = digits.length - 3 + scopeIndex;

    if (targetIndex < 0) return; // Should not happen with valid inputs

    let val = parseInt(digits[targetIndex], 10);
    if (isNaN(val)) val = 0;

    // Toggle bit
    if (val & bitValue) {
      val &= ~bitValue;
    } else {
      val |= bitValue;
    }

    digits[targetIndex] = val.toString();
    setMode(digits.join(''));
  };

  const perms = getPermissions();

  return (
    <Modal
      isOpen={true}
      onRequestClose={onClose}
      contentLabel="Berechtigungen ändern"
      className="modal-dialog"
      overlayClassName="modal-backdrop"
    >
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Berechtigungen ändern</h5>
          <button type="button" className="close" onClick={onClose}>
            <span>&times;</span>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="mode">Berechtigungen (Oktal)</label>
              <input
                type="text"
                id="mode"
                className="form-control"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                placeholder="z.B. 755"
                autoFocus
              />
              <small className="form-text text-muted">
                Aktuelle Berechtigungen: {item.mode}
              </small>
            </div>

            <div className="form-group">
              <label>Berechtigungs-Details:</label>
              <table className="table table-sm table-bordered text-center">
                <thead>
                  <tr>
                    <th></th>
                    <th>Lesen (r)</th>
                    <th>Schreiben (w)</th>
                    <th>Ausführen (x)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th className="text-left">Besitzer</th>
                    <td>
                      <input
                        type="checkbox"
                        checked={perms.user.r}
                        onChange={() => togglePermission(0, 4)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={perms.user.w}
                        onChange={() => togglePermission(0, 2)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={perms.user.x}
                        onChange={() => togglePermission(0, 1)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th className="text-left">Gruppe</th>
                    <td>
                      <input
                        type="checkbox"
                        checked={perms.group.r}
                        onChange={() => togglePermission(1, 4)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={perms.group.w}
                        onChange={() => togglePermission(1, 2)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={perms.group.x}
                        onChange={() => togglePermission(1, 1)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th className="text-left">Andere</th>
                    <td>
                      <input
                        type="checkbox"
                        checked={perms.other.r}
                        onChange={() => togglePermission(2, 4)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={perms.other.w}
                        onChange={() => togglePermission(2, 2)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={perms.other.x}
                        onChange={() => togglePermission(2, 1)}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="form-group">
              <label>Voreinstellungen:</label>
              <div className="btn-group-vertical w-100">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary text-left"
                  onClick={() => handlePresetClick('0644')}
                >
                  0644 - Datei (rw-r--r--)
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary text-left"
                  onClick={() => handlePresetClick('0755')}
                >
                  0755 - Ordner / Ausführbar (rwxr-xr-x)
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary text-left"
                  onClick={() => handlePresetClick('0600')}
                >
                  0600 - Nur Besitzer (rw-------)
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary text-left"
                  onClick={() => handlePresetClick('0777')}
                >
                  0777 - Alle Rechte (rwxrwxrwx)
                </button>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={chmodItemMutation.isLoading}
            >
              {chmodItemMutation.isLoading ? 'Ändere...' : 'Ändern'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

ChmodModal.propTypes = {
  item: PropTypes.object.isRequired,
  currentPath: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  baseApiUrl: PropTypes.string,
};

export default ChmodModal;